// The game's sounds, made in code (owner 2026-10-17: the legacy ogg / mp3 files played late; "zaimplementuj
// dźwięk używając natywnego api audio", paper by default, plastic as the second bank). Each sound is a short
// PCM buffer computed here once (noise through simple filters and envelopes, a few damped tones) and played
// by the Web Audio API: a buffer source starts on the audio clock at once, with no file to fetch or decode.
//
// Banks: `paper` and `plastic`. Paper (owner 2026-10-17, after two takes: the first "nie przypomina papieru
// ... papier wydaje niższy dźwięk"; the second's deal "brzmi jak z karabinu", but its landing "try try try"
// was right): one card is ONE soft, dull pat (low noise and a little body, no click, no slide before it);
// the deal is a gentle, rough rustle under the whole deal with quiet, slightly uneven pats as the cards
// land. Plastic: a short bright swish and a click with a little ring for a card; for the deal the same
// swish quietly per card and soft clicks. The deal's sound is built for its shape (cards, the time between
// two of them, the flight; the view's: 45 ms and 360 ms at the start of a game, 10 cards at once in 180 ms
// for a Spider stock deal), started on the audio clock as the view starts, so it ends with the last card.
// `win` is a short bell arpeggio, the same in both banks.
//
// No Math.random here: the deal's shuffle pattern must stay the only use of it before the first deal
// (e2e pattern parity); the noise comes from a seeded generator, so every buffer is the same on every load.
export type SynthBank = 'paper' | 'plastic';
import { MAHJONG_DEAL_SCALE } from '../engine/motion';

export type SynthName = 'drop' | 'deal' | 'win';
/** A deal for its sound: `cards` sent `perCardMs` apart, each landing `flightMs` after it leaves. */
export interface DealShape {
  cards: number;
  perCardMs: number;
  flightMs: number;
}

/** mulberry32: a small seeded PRNG for the noise. */
function prng(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

type Kind = 'lowpass' | 'highpass' | 'bandpass';

/** An RBJ biquad run over `x` in place. */
function biquad(x: Float32Array, rate: number, kind: Kind, freq: number, q: number): Float32Array {
  const w = (2 * Math.PI * freq) / rate;
  const cos = Math.cos(w);
  const alpha = Math.sin(w) / (2 * q);
  let b0: number, b1: number, b2: number;
  if (kind === 'lowpass') [b0, b1, b2] = [(1 - cos) / 2, 1 - cos, (1 - cos) / 2];
  else if (kind === 'highpass') [b0, b1, b2] = [(1 + cos) / 2, -(1 + cos), (1 + cos) / 2];
  else [b0, b1, b2] = [alpha, 0, -alpha];
  const a0 = 1 + alpha;
  const a1 = -2 * cos;
  const a2 = 1 - alpha;
  let x1 = 0,
    x2 = 0,
    y1 = 0,
    y2 = 0;
  for (let i = 0; i < x.length; i++) {
    const x0 = x[i]!;
    const y0 = (b0 * x0 + b1 * x1 + b2 * x2 - a1 * y1 - a2 * y2) / a0;
    x2 = x1;
    x1 = x0;
    y2 = y1;
    y1 = y0;
    x[i] = y0;
  }
  return x;
}

const noise = (n: number, rnd: () => number) => {
  const x = new Float32Array(n);
  for (let i = 0; i < n; i++) x[i] = rnd() * 2 - 1;
  return x;
};

/** Adds `src` times `env(t)` into `out` from `at` seconds. */
function mix(out: Float32Array, rate: number, src: Float32Array, at: number, gain: number, env: (t: number) => number) {
  const start = Math.round(at * rate);
  for (let i = 0; i < src.length && start + i < out.length; i++) out[start + i]! += src[i]! * gain * env(i / rate);
}

/** Friction: a slow random wobble (0.4-1) that makes a rustle rough rather than a smooth hiss. */
function friction(n: number, rate: number, rnd: () => number, speed: number): Float32Array {
  const w = biquad(noise(n, rnd), rate, 'lowpass', speed, 0.7);
  let max = 0;
  for (const v of w) max = Math.max(max, Math.abs(v));
  for (let i = 0; i < n; i++) w[i] = 0.4 + 0.6 * Math.abs(w[i]! / (max || 1));
  return w;
}

/** Paper sliding over paper: dark noise (most of it 250-1800 Hz), rough, rising and falling over `len`. */
function rustle(
  out: Float32Array,
  rate: number,
  rnd: () => number,
  at: number,
  len: number,
  gain: number,
  tone = 1,
  rise = 0.012,
) {
  const n = Math.round(len * rate);
  const src = biquad(
    biquad(biquad(noise(n, rnd), rate, 'lowpass', 1800 * tone, 0.6), rate, 'lowpass', 2600 * tone, 0.6),
    rate,
    'highpass',
    260,
    0.7,
  );
  const rough = friction(n, rate, rnd, 140);
  for (let i = 0; i < n; i++) src[i] = src[i]! * rough[i]!;
  mix(out, rate, src, at, gain, (t) => Math.min(1, t / rise) * Math.sin((Math.PI * Math.min(t, len)) / len) ** 0.6);
}

/** A plastic swish: brighter, smoother band-limited noise. */
function swish(
  out: Float32Array,
  rate: number,
  rnd: () => number,
  at: number,
  len: number,
  freq: number,
  q: number,
  gain: number,
) {
  const n = Math.round(len * rate);
  const src = biquad(biquad(noise(n, rnd), rate, 'bandpass', freq, q), rate, 'highpass', 700, 0.7);
  mix(out, rate, src, at, gain, (t) => Math.sin((Math.PI * t) / len) ** 1.5);
}

/** A hit: a noise burst with an exponential decay (the card laid down). */
function hit(
  out: Float32Array,
  rate: number,
  rnd: () => number,
  at: number,
  kind: Kind,
  freq: number,
  decay: number,
  gain: number,
  attack = 0.0015,
) {
  const n = Math.round(decay * 8 * rate);
  const src = biquad(noise(n, rnd), rate, kind, freq, 0.8);
  mix(out, rate, src, at, gain, (t) => Math.min(1, t / attack) * Math.exp(-t / decay));
}

/** A damped sine (a plastic card's ring, a bell partial, the body of a pat). */
function tone(out: Float32Array, rate: number, at: number, freq: number, decay: number, gain: number, attack = 0.002) {
  const n = Math.round(decay * 8 * rate);
  const src = new Float32Array(n);
  for (let i = 0; i < n; i++) src[i] = Math.sin((2 * Math.PI * freq * i) / rate);
  mix(out, rate, src, at, gain, (t) => Math.min(1, t / attack) * Math.exp(-t / decay));
}

/** A paper card laid on paper: a soft, dull pat (low noise and a little body, no click). `light`: less
 *  bass and a shorter tail, for a single card (owner 2026-10-17: the full pat alone was "zbyt kick"). */
function pat(out: Float32Array, rate: number, rnd: () => number, at: number, gain: number, light = false) {
  if (light) {
    // a soft attack (6 ms) and less of the low end: a tap, not a kick (owner 2026-10-17, second note)
    hit(out, rate, rnd, at, 'bandpass', 1100, 0.009, gain, 0.006);
    hit(out, rate, rnd, at, 'bandpass', 1700, 0.005, gain * 0.35, 0.004);
    return;
  }
  hit(out, rate, rnd, at, 'lowpass', 520, 0.016, gain, 0.003);
  hit(out, rate, rnd, at, 'bandpass', 1100, 0.007, gain * 0.35, 0.002);
  tone(out, rate, at, 150, 0.014, gain * 0.25, 0.003);
}

/** Scales the buffer so its peak is `peak`, with 4 ms fades at both ends (no clicks). */
function finish(x: Float32Array, rate: number, peak: number): Float32Array {
  let max = 0;
  for (const v of x) max = Math.max(max, Math.abs(v));
  const k = max > 0 ? peak / max : 0;
  const fade = Math.round(0.004 * rate);
  for (let i = 0; i < x.length; i++) {
    const edge = Math.min(1, i / fade, (x.length - 1 - i) / fade);
    x[i] = x[i]! * k * edge;
  }
  return x;
}

/** One card moved: the take (a short rustle / swish as it leaves its place) and, `flightMs` later, the light
 *  tap as it lands (owner 2026-10-17: "najpierw szum wzięcia karty a po chwili lekkie tapnięcie", in step
 *  with the glide and the 3D turn: 400 ms from the stock, 320 to a foundation, 180 to a column). `v` varies
 *  it a little (three takes per bank). */
function drop(bank: SynthBank, rate: number, v: number, flightMs = 180): Float32Array {
  const rnd = prng(1000 + v * 17 + (bank === 'plastic' ? 500 : 0));
  const flight = flightMs / 1000;
  const land = Math.max(0.06, flight - 0.015); // the tap a hair before the end: the ear takes it as the landing
  const out = new Float32Array(Math.round((land + 0.1) * rate));
  const take = Math.min(0.1, flight * 0.45) + v * 0.006;
  if (bank === 'paper') {
    // softer than the first takes (owner 2026-10-17: "szum delikatniejszy i ten kik też"): a darker, slower
    // rising rustle, the light tap, and the whole sound quieter (finish below)
    rustle(out, rate, rnd, 0.005, take, 0.16, 0.75 + v * 0.04, 0.03);
    pat(out, rate, rnd, land, 0.22, true);
  } else {
    swish(out, rate, rnd, 0.005, Math.min(0.05, take), 4200 + v * 300, 1.1, 0.4);
    hit(out, rate, rnd, land, 'highpass', 1800, 0.004, 1);
    tone(out, rate, land, 2350 + v * 60, 0.012, 0.35);
    tone(out, rate, land, 3900 + v * 90, 0.007, 0.2);
  }
  return finish(out, rate, bank === 'paper' ? 0.22 : 0.55);
}

/** The whole deal, for its shape: a gentle rustle (paper) or quiet swishes (plastic) while the cards leave the
 *  deck, and soft, slightly uneven pats / clicks as they land. */
function deal(bank: SynthBank, rate: number, shape: DealShape): Float32Array {
  const rnd = prng(3000 + shape.cards * 7 + shape.perCardMs + (bank === 'plastic' ? 500 : 0));
  const per = shape.perCardMs / 1000;
  const flight = shape.flightMs / 1000;
  const sending = Math.max(0, shape.cards - 1) * per;
  const out = new Float32Array(Math.round((sending + flight + 0.12) * rate));
  // the cards land over `spread` even when they are all sent at once (Spider), so it is never one hit
  const spread = per > 0 ? 0 : 0.03;
  if (bank === 'paper') {
    rustle(out, rate, rnd, 0, sending + flight * 0.8, 0.22, 0.85);
  }
  for (let i = 0; i < shape.cards; i++) {
    const at = i * per;
    const land = at + flight - 0.02 + (rnd() - 0.5) * 0.012 + rnd() * spread;
    const g = 0.5 + rnd() * 0.5;
    if (bank === 'paper') pat(out, rate, rnd, Math.max(0.005, land), 0.32 * g);
    else {
      swish(out, rate, rnd, at, 0.035, 4300 + rnd() * 400, 1.1, 0.12 * g);
      hit(out, rate, rnd, Math.max(0.005, land), 'highpass', 1900, 0.003, 0.3 * g);
      tone(out, rate, Math.max(0.005, land), 2250 + rnd() * 150, 0.008, 0.09 * g);
    }
  }
  return finish(out, rate, bank === 'paper' ? 0.3 : 0.25);
}

/** The win: a soft bell arpeggio, C E G C, the last note held. */
function win(rate: number): Float32Array {
  const out = new Float32Array(Math.round(1.6 * rate));
  const notes = [523.25, 659.25, 783.99, 1046.5];
  notes.forEach((f, i) => {
    const at = i * 0.11;
    const decay = i === notes.length - 1 ? 0.42 : 0.22;
    tone(out, rate, at, f, decay, 0.6, 0.006);
    tone(out, rate, at, f * 2, decay * 0.5, 0.18, 0.006);
    tone(out, rate, at, f * 3.01, decay * 0.3, 0.06, 0.006);
  });
  return finish(out, rate, 0.45);
}

/** The PCM generators, for the unit test (sound/__tests__). */
export const render = { drop, deal, win };

type Ctx = AudioContext;
let ctx: Ctx | null = null;
let unlockInstalled = false;
/** The page went to the background (Safari minimised, another app, the screen locked). iOS interrupts the
 *  AudioContext then, and back in front it often cannot be resumed, or reports `running` and stays silent
 *  (owner 2026-10-17: after minimising Safari no sound played any more). The first gesture after it builds a
 *  new context; the PCM buffers are kept (an AudioBuffer works in any context of the same sample rate). */
let stale = false;

/** `?sound-debug` in the address: a small box in the corner with the audio state, for checking on a device
 *  (the owner's iPad: no sound after some steps; no remote console there). */
const DEBUG = typeof location !== 'undefined' && location.search.includes('sound-debug');
let debugBox: HTMLElement | null = null;
const debugLines: string[] = [];
let contexts = 0;
function debug(line: string) {
  if (!DEBUG) return;
  debugLines.push(`${(performance.now() / 1000).toFixed(1)} ${line}`);
  if (debugLines.length > 12) debugLines.shift();
  if (!debugBox) {
    debugBox = document.createElement('pre');
    debugBox.style.cssText =
      'position:fixed;left:4px;top:60px;z-index:99999;margin:0;padding:6px;max-width:60vw;font:11px/1.3 monospace;color:#0f0;background:rgb(0 0 0/80%);pointer-events:none;white-space:pre-wrap';
    document.body.appendChild(debugBox);
  }
  debugBox.textContent = `ctx #${contexts} ${ctx?.state ?? 'none'} ${ctx?.sampleRate ?? ''} Hz\n${debugLines.join('\n')}`;
}
const buffers = new Map<string, AudioBuffer>();
let take = 0;
/** The deal's playing source: a new deal stops the rest of the old one. */
let dealing: AudioBufferSourceNode | null = null;

const dropBuffer = (c: Ctx, bank: SynthBank, v: number, flightMs: number) =>
  buffer(c, `${bank}/drop/${v}/${flightMs}`, () => drop(bank, c.sampleRate, v, flightMs));

const dealBuffer = (c: Ctx, bank: SynthBank, shape: DealShape) =>
  buffer(c, `${bank}/deal/${shape.cards}/${shape.perCardMs}/${shape.flightMs}`, () => deal(bank, c.sampleRate, shape));

function context(): Ctx | null {
  if (ctx) return ctx;
  const old = buffers.size ? buffers.values().next().value : undefined;
  try {
    const AC =
      window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return null;
    const c = new AC({ latencyHint: 'interactive' });
    ctx = c;
    contexts += 1;
    c.addEventListener('statechange', () => debug(`state ${c.state}`));
    debug('new context');
    if (old && old.sampleRate !== c.sampleRate) {
      buffers.clear(); // made again at the new rate, from the first step
      buildStep = 0;
    }
  } catch (e) {
    ctx = null;
    debug(`new context FAILED ${String(e)}`);
  }
  return ctx;
}

function buffer(c: Ctx, key: string, make: () => Float32Array): AudioBuffer {
  let b = buffers.get(key);
  if (!b) {
    const data = make();
    b = c.createBuffer(1, data.length, c.sampleRate);
    // Safari before 14.1 has no copyToChannel (an iPad on iOS 14.0-14.4 stopped at the start, 2026-10-25)
    if (typeof b.copyToChannel === 'function') b.copyToChannel(data as Float32Array<ArrayBuffer>, 0);
    else b.getChannelData(0).set(data);
    buffers.set(key, b);
  }
  return b;
}

/** The buffers built ahead of the first play (so the first card is not late either), one per step. */
const buildSteps = (c: Ctx, bank: SynthBank): (() => unknown)[] => [
  ...[160, 180, 320, 400].flatMap((ms) => [0, 1, 2].map((v) => () => dropBuffer(c, bank, v, ms))),
  // the start of a game's deals (Klondike 28 cards, Double Klondike 45, Spider 54, Forty Thieves 40)
  ...[28, 40, 45, 54].map((cards) => () => dealBuffer(c, bank, { cards, perCardMs: 45, flightMs: 360 })),
  () => buffer(c, 'win', () => win(c.sampleRate)),
  // Mahjong's 144 tiles at their quicker pace (engine/motion.ts MAHJONG_DEAL_SCALE): built at the deal, its 3 s of sound
  // started late on an iPhone and outlasted the tiles (owner 2026-10-24). The largest, so the last.
  () => dealBuffer(c, bank, { cards: 144, perCardMs: Math.round(45 * MAHJONG_DEAL_SCALE), flightMs: 360 }),
];

/** The bank still to build. The context and the buffers never come in one go at the start (2026-11-01, Web Vitals, measured
 *  on a desktop): they took 350 of the entry module's 353 ms, 0.4-1.3 s on a slower computer, and the table's picture
 *  waited for that task to reach the screen. No sound can play before a gesture anyway. They are built after the first
 *  deal (`startBuilding`) or the first gesture, whichever comes first, one step per idle moment. A plain timer would only
 *  move the long task, and the context is not left for the first gesture: its 160-280 ms would land in the first click. */
let building: SynthBank | null = null;
let buildStep = 0;
let buildHeld = true;
let buildSliceWaiting = false;

function nextBuildSlice() {
  if (buildHeld || buildSliceWaiting || !building) return;
  buildSliceWaiting = true;
  // Safari has no requestIdleCallback
  if (typeof requestIdleCallback === 'function') requestIdleCallback(buildSlice, { timeout: 500 });
  else setTimeout(buildSlice, 30);
}

function buildSlice() {
  buildSliceWaiting = false;
  if (!building) return;
  const hadContext = !!ctx; // the context is a step of its own
  const c = context();
  if (!c) {
    building = null;
    return;
  }
  if (hadContext) {
    const step = buildSteps(c, building)[buildStep++];
    if (!step) {
      building = null;
      return;
    }
    try {
      step();
    } catch (e) {
      building = null;
      debug(`build FAILED ${String(e)}`);
      return;
    }
  }
  nextBuildSlice();
}

/** Asks for the bank's buffers; nothing heavy runs here (see `building`). The unlock is at once, or the iOS one is lost. */
export function prepare(bank: SynthBank): void {
  installUnlock();
  if (building === bank) return;
  building = bank;
  buildStep = 0;
  nextBuildSlice();
}

/** The first deal is on its way: the buffers may be built. */
export function startBuilding(): void {
  buildHeld = false;
  nextBuildSlice();
}

const GESTURE_KEY = 'solitaire_storage:sound-gesture';
let gestureKept = false;
/** A gesture in this tab, also before a reload: Chrome lets a page reloaded after a gesture start its audio, so the deal
 *  after a win's reload has its sound; on a first visit the browser would keep the deal silent anyway. */
function keepGesture() {
  if (gestureKept) return;
  gestureKept = true;
  try {
    sessionStorage.setItem(GESTURE_KEY, '1');
  } catch {
    /* no storage */
  }
}
function gestureInThisTab(): boolean {
  if (gestureKept) return true;
  try {
    return sessionStorage.getItem(GESTURE_KEY) === '1';
  } catch {
    return false;
  }
}

/** Browsers start an AudioContext suspended until the page gets a gesture (iOS: and a sound started in it),
 *  and iOS interrupts it in the background: every gesture checks it (cheap when it runs), and the first one
 *  after the page was in the background replaces it (see `stale`). */
function installUnlock() {
  if (unlockInstalled) return;
  unlockInstalled = true;
  document.addEventListener('visibilitychange', () => {
    debug(`visibility ${document.visibilityState}`);
    if (document.hidden) stale = true;
  });
  addEventListener('pagehide', () => {
    debug('pagehide');
    stale = true;
  });
  // Every gesture: resume and start a silent sound (iOS unlocks the audio only with a sound started in a
  // gesture; the way that worked on the owner's iPad). After the page was in the background (`stale`), the
  // context is replaced only if it is still not running a moment after such a gesture.
  const unlock = (ev: Event) => {
    debug(`${ev.type} (${ctx?.state ?? 'none'}${stale ? ', stale' : ''})`);
    keepGesture();
    startBuilding();
    const c = context();
    if (!c) return;
    if (c.state === 'running' && !stale) return;
    void c.resume().then(
      () => debug('resume ok'),
      (e) => debug(`resume FAILED ${String(e)}`),
    );
    try {
      const s = c.createBufferSource();
      s.buffer = c.createBuffer(1, 1, c.sampleRate);
      s.connect(c.destination);
      s.start();
    } catch {
      /* ignore */
    }
    if (stale && ev.type !== 'pointerdown') {
      stale = false;
      setTimeout(() => {
        if (ctx !== c || c.state === 'running') return;
        debug('still not running after the gesture: a new context');
        ctx = null;
        void c.close().catch(() => undefined);
      }, 300);
    }
  };
  for (const ev of ['pointerdown', 'touchend', 'keydown'] as const) addEventListener(ev, unlock, true);
}

function start(c: Ctx, b: AudioBuffer, when = 0, gain = 1): AudioBufferSourceNode {
  const src = c.createBufferSource();
  src.buffer = b;
  if (gain === 1) src.connect(c.destination);
  else {
    const g = c.createGain();
    g.gain.value = gain;
    src.connect(g).connect(c.destination);
  }
  src.start(when);
  return src;
}

/** `shape`: the deal's cards and timing; for a drop only its `flightMs` counts. */
export function play(bank: SynthBank, name: SynthName, shape?: Partial<DealShape>): void {
  // the deal at the start of a first visit: silent all the same (below), so no context is made for it (see `building`)
  if (name === 'deal' && !ctx && !gestureInThisTab()) return;
  const c = context();
  if (!c) return;
  try {
    // 'suspended' before a gesture, and Safari's 'interrupted' (another tab or a video ad took the audio)
    if (c.state !== 'running') void c.resume().catch(() => undefined);
    if (!buffers.has('win')) prepare(bank); // first play, or a new context at another sample rate; only what this play needs is made now
    if (name === 'deal') {
      try {
        dealing?.stop();
      } catch {
        /* already over */
      }
      dealing = null;
      // Only when the audio already runs: a deal sound held by the browser until the first tap (the deal on
      // page load) would start late, out of step with the cards; a deal is silent rather than surprising
      // (owner 2026-10-17: "żeby nie było dziwnych, zaskakujących dźwięków").
      debug(`deal ${shape?.cards ?? 0} cards (${c.state}${c.state === 'running' ? '' : ': silent'})`);
      if (!shape?.cards || c.state !== 'running') return;
      dealing = start(
        c,
        dealBuffer(c, bank, { cards: shape.cards, perCardMs: shape.perCardMs ?? 0, flightMs: shape.flightMs ?? 180 }),
      );
      return;
    }
    const b = name === 'win' ? buffer(c, 'win', () => win(c.sampleRate)) : dropBuffer(c, bank, take++ % 3, shape?.flightMs ?? 180);
    start(c, b);
    debug(`play ${bank}/${name} (${c.state})`);
  } catch (e) {
    debug(`play FAILED ${String(e)}`); /* otherwise ignored: no audio output */
  }
}
